import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { useWebsiteImage } from '../hooks/useWebsiteImages';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Dynamic logo fetching
  const logo = useWebsiteImage('website_logo');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1C1917] flex items-center justify-center relative overflow-hidden" data-testid="login-page">
      {/* Background effects */}
      <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-[#2D5F3F]/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-[300px] h-[300px] bg-[#D4A574]/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="hero-grid" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md px-4 sm:px-6 relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-6 sm:mb-8">
          {logo.loading ? (
            <div className="w-9 h-9 rounded-lg bg-white/10 animate-pulse" />
          ) : logo.image ? (
            <img
              src={logo.image}
              alt={logo.alt_text || "Subhouz Logo"}
              className="w-9 h-9 object-contain"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-[#2D5F3F] flex items-center justify-center">
              <span className="text-white font-bold text-base" style={{ fontFamily: "'Fraunces', serif" }}>S</span>
            </div>
          )}
          <span className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Subhouz</span>
        </div>

        <div className="glass-card p-6 sm:p-8 rounded-2xl sm:rounded-3xl">
          <h2 className="text-xl font-semibold text-white text-center mb-1" style={{ fontFamily: "'Fraunces', serif" }}>Admin Portal</h2>
          <p className="text-sm text-[#A39889] text-center mb-6">Sign in to manage your properties</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-sm text-red-400" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-slate-300">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@subhouz.com"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 rounded-lg focus:ring-blue-600 focus:border-blue-600"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-slate-300">Password</Label>
                <button
                  type="button"
                  onClick={() => setForgotModalOpen(true)}
                  className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 rounded-lg focus:ring-blue-600 focus:border-blue-600 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#2D5F3F] hover:bg-[#1F4A2E] text-white h-11 rounded-lg font-semibold shadow-lg shadow-[#2D5F3F]/25 mt-2"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <span className="flex items-center gap-2">Sign In <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/5">
            <p className="text-xs text-[#6B5E54] text-center">
              This is the admin portal for property management.
            </p>
          </div>
        </div>

        <button onClick={() => navigate('/')} className="mt-6 text-sm text-slate-500 hover:text-slate-300 transition-colors block mx-auto">
          &larr; Back to home
        </button>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          open={forgotModalOpen}
          onClose={() => setForgotModalOpen(false)}
        />
      </motion.div>
    </div>
  );
}
